/**
 * Tenant API Service - Wrapper for generated OpenAPI client
 *
 * This service provides a facade over the generated TenantApiService
 * to maintain backward compatibility with the tenant.store.ts interface.
 */
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TenantApiService as GeneratedTenantService } from '@api/api/tenant.service';
import {
  TenantInfo,
  SwitchTenantResponse as ApiSwitchTenantResponse,
  SwitchTenantRequest as ApiSwitchTenantRequest
} from '@api/model/models';
import {
  Tenant,
  SwitchTenantRequest,
  SwitchTenantResponse
} from '../../models/tenant.model';
import { LoggerService } from '@core/services/logger.service';

/**
 * Available tenants response with properly typed tenants
 */
export interface AvailableTenantsResponseLocal {
  tenants: Tenant[];
}

/**
 * Transform API TenantInfo to local Tenant interface
 */
function toTenant(info: TenantInfo): Tenant {
  return {
    id: info.id,
    name: info.name,
    code: info.code,
    logo: info.logo ?? undefined,
    status: info.active ? 'active' : 'inactive',
    features: info.features ?? [],
    settings: info.settings ?? undefined,
    createdAt: new Date(info.createdAt),
    updatedAt: new Date(info.updatedAt)
  };
}

/**
 * Transform API SwitchTenantResponse to local interface
 */
function toSwitchTenantResponse(resp: ApiSwitchTenantResponse): SwitchTenantResponse {
  // The API response.tenant uses snake_case properties from the Tenant model
  const apiTenant = resp.tenant;
  return {
    success: resp.success,
    tenant: {
      id: apiTenant.id,
      name: apiTenant.name,
      code: apiTenant.code,
      logo: apiTenant.logo ?? undefined,
      status: apiTenant.active ? 'active' : 'inactive',
      features: apiTenant.features ?? [],
      settings: apiTenant.settings as Tenant['settings'],
      createdAt: new Date(apiTenant.created_at),
      updatedAt: new Date(apiTenant.updated_at)
    },
    token: resp.token ?? undefined,
    permissions: resp.permissions
  };
}

@Injectable({
  providedIn: 'root'
})
export class TenantApiService {
  private readonly api = inject(GeneratedTenantService);
  private readonly logger = inject(LoggerService);

  /**
   * Get available tenants for the current user
   */
  getAvailableTenants(): Observable<AvailableTenantsResponseLocal> {
    return this.api.listAvailableTenants().pipe(
      map(response => ({
        tenants: response.tenants.map(toTenant)
      })),
      catchError(error => {
        this.logger.error('Failed to get available tenants', { component: 'TenantApiService' }, error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get tenant by ID
   */
  getTenantById(tenantId: string): Observable<Tenant> {
    return this.api.getTenant(tenantId).pipe(
      map(toTenant),
      catchError(error => {
        this.logger.error('Failed to get tenant by ID', { component: 'TenantApiService', tenantId }, error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Switch to a different tenant
   */
  switchTenant(request: SwitchTenantRequest): Observable<SwitchTenantResponse> {
    const apiRequest: ApiSwitchTenantRequest = {
      tenant_id: request.tenant_id,
      reason: request.reason
    };
    return this.api.switchTenant(apiRequest).pipe(
      map(toSwitchTenantResponse),
      catchError(error => {
        this.logger.error('Failed to switch tenant', { component: 'TenantApiService', tenantId: request.tenant_id }, error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get current tenant
   */
  getCurrentTenant(): Observable<Tenant> {
    return this.api.getCurrentTenant().pipe(
      map(toTenant),
      catchError(error => {
        this.logger.error('Failed to get current tenant', { component: 'TenantApiService' }, error);
        return throwError(() => error);
      })
    );
  }
}
